import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { usePortalNavigate } from '@/hooks/usePortalPath';
import type { Client } from '@core/generated/gql-types';
import {
  TemplateTerm,
  useTemplateStore,
} from '@core/template/useTemplateStore';
import Breadcrumbs from '@/components/Breadcrumbs';
import Tabs from '@/components/Tabs';
import useFeatureView from '@/hooks/useFeatureView';
import { ClientUsers } from '@/modules/clientUsers';
import ClientActivity from '@/modules/clients/components/ClientActivity';
import ClientBilling from '@/modules/clients/components/ClientBilling';
import ClientDetailsCard from '@/modules/clients/components/ClientDetailsCard';
import ClientDocuments from '@/modules/clients/components/ClientDocuments';
import ClientHeader from '@/modules/clients/components/ClientHeader';
import ClientJobs from '@/modules/clients/components/ClientJobs';
import ClientWorkers from '@/modules/clients/components/ClientWorkers';
import ClientRates from '@/modules/clients/components/rates/ClientRates';
import { ClientSites } from '@/modules/sites';
import { useClientDetailData } from '@/modules/clients/hooks/useClientDetailData';
import {
  getClientDetailTabs,
  getClientDetailView,
  type ClientDetailTabKey,
} from '@/modules/clients/views/clientDetail.view';

const ClientPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = usePortalNavigate();
  const view = useFeatureView(getClientDetailView);
  const { client, isLoading, error, refetchClient, updateClientDetails } =
    useClientDetailData(view, id);
  const { getTerm } = useTemplateStore();
  const clientTabs = getClientDetailTabs(view.tabs);
  const [selectedTab, setSelectedTab] = useState<ClientDetailTabKey>('details');

  if (isLoading) {
    return (
      <div className="p-4">
        <div className="animate-pulse">
          <div className="mb-4 h-8 w-1/4 rounded-sm bg-gray-200"></div>
          <div className="space-y-3">
            <div className="h-4 w-3/4 rounded-sm bg-gray-200"></div>
            <div className="h-4 w-1/2 rounded-sm bg-gray-200"></div>
            <div className="h-4 w-2/3 rounded-sm bg-gray-200"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <div className="rounded-md border border-red-200 bg-red-50 p-4">
          <p className="text-red-600">{error}</p>
          <button
            onClick={() => navigate('/clients')}
            className="mt-4 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-hidden"
          >
            Back to {getTerm(TemplateTerm.CLIENT, true)}
          </button>
        </div>
      </div>
    );
  }

  if (!client) {
    return null;
  }

  const handleLogoUpdate = async () => {
    if (view.actions.showLogoUpdate) {
      await refetchClient();
    }
  };

  const handleUpdateClient = async (updatedClient: Partial<Client>) => {
    if (!view.actions.showUpdateClient) {
      return { success: false, error: 'Update not allowed' };
    }
    return updateClientDetails(updatedClient);
  };

  return (
    <div className="flex h-full flex-col p-4">
      <Breadcrumbs
        items={[
          { label: getTerm(TemplateTerm.CLIENT, true), to: '/clients' },
          { label: client.name ?? getTerm(TemplateTerm.CLIENT, false) },
        ]}
        className="mb-4"
      />
      <ClientHeader
        id={client.id}
        name={client.name ?? getTerm(TemplateTerm.CLIENT, false)}
        logoUrl={client.logoUrl ?? undefined}
        status={client.status ?? undefined}
        onLogoUpdate={handleLogoUpdate}
      />
      <Tabs
        tabs={clientTabs}
        selectedTabKey={selectedTab}
        onTabChange={(key) => setSelectedTab(key as ClientDetailTabKey)}
      >
        {selectedTab === 'details' && (
          <ClientDetailsCard
            client={client}
            onUpdateClient={handleUpdateClient}
            onLogoUpdate={handleLogoUpdate}
          />
        )}
        {selectedTab === 'sites' && <ClientSites clientId={id ?? ''} />}
        {selectedTab === 'billing' && <ClientBilling clientId={id} />}
        {selectedTab === 'users' && <ClientUsers clientId={id} />}
        {selectedTab === 'activity' && <ClientActivity clientId={id} />}
        {selectedTab === 'jobs' && <ClientJobs clientId={id || ''} />}
        {selectedTab === 'candidates' && <ClientWorkers clientId={id ?? ''} />}
        {selectedTab === 'rates' && (
          <ClientRates client={client} onUpdateClient={handleUpdateClient} />
        )}
        {selectedTab === 'documents' && (
          <ClientDocuments clientId={id ?? ''} clientName={client.name} />
        )}
      </Tabs>
    </div>
  );
};

export default ClientPage;
